import { set, sub } from 'date-fns';
import React, { useEffect, useRef, useState } from 'react';
import { getAllParameters } from '../../../../server/server';

const Counter = ({ name, value, type }: any) => {
	let subText = '';
	const key = name; // preserve original key
	let displayName = name;
	const [npaaFrom, setNpaaFrom] = useState<any[]>([]);

	function getCurrentAndPreviousQuarter(): {
		current: string;
		previous: string;
	} {
		const now = new Date();
		const quarter = Math.floor(now.getMonth() / 3) + 1; // 1..4
		const romanMap = ['I', 'II', 'III', 'IV'];
		const current = `${romanMap[quarter - 1]}/${now.getFullYear()}`;

		let prevYear = now.getFullYear();
		let prevQuarter = quarter - 1;
		if (prevQuarter === 0) {
			prevQuarter = 4;
			prevYear -= 1;
		}
		const previous = `${romanMap[prevQuarter - 1]}/${prevYear}`;

		return { current, previous };
	}

	useEffect(() => {
		getAllParameters().then((response: any) => {
			const npaaFrom = response.data.find((p: any) => p.name === 'npaa-from')?.value;

			setNpaaFrom(npaaFrom);
		});
	}, []);

	const { current, previous } = getCurrentAndPreviousQuarter();

	const getColorPallete = (name: string) => {
		switch (name) {
			case 'totalOverallCount':
				return {
					bgClass: 'bg-[#F0D9FF] border border-[#9932CC] rounded-[8px]',
					numberClass: 'text-[#4B0082] text-[32px]',
					mainTextClass: 'text-[#374151] text-[14px]',
					subTextClass: 'text-[#6B7280] text-[12px]',
				};
			case 'totalPlannedCount':
				return {
					bgClass: 'bg-[#F0F9FF] border border-[#BFDBFE] rounded-[8px]',
					numberClass: 'text-[#1D4ED8] text-[32px]',
					mainTextClass: 'text-[#374151] text-[14px]',
					subTextClass: 'text-[#6B7280] text-[12px]',
				};
			case 'realisedCount':
				return {
					bgClass: 'bg-[#F0FDF4] border border-[#BBF7D0] rounded-[8px]',
					numberClass: 'text-[#166534] text-[32px]',
					mainTextClass: 'text-[#374151] text-[14px]',
					subTextClass: 'text-[#6B7280] text-[12px]',
				};
			case 'previousQuarterUnrealisedCount':
				return {
					bgClass: 'bg-[#FEF2F2] border border-[#FECACA] rounded-[8px]',
					numberClass: 'text-[#DC2626] text-[32px]',
					mainTextClass: 'text-[#374151] text-[14px]',
					subTextClass: 'text-[#6B7280] text-[12px]',
				};
			case 'totalUnrealisedCount':
				return {
					bgClass: 'bg-[#FFFBEB] border border-[#FED7AA] rounded-[8px]',
					numberClass: 'text-[#EA580C] text-[32px]',
					mainTextClass: 'text-[#374151] text-[14px]',
					subTextClass: 'text-[#6B7280] text-[12px]',
				};
			default:
				return {
					bgClass: 'bg-gray-100',
					numberClass: 'text-gray-800',
					mainTextClass: 'text-gray-800',
					subTextClass: 'text-gray-600',
				};
		}
	};

	switch (key) {
		case 'totalOverallCount':
			displayName = type === 'measure' ? 'Укупно планираних мера' : 'Укупан број прописа ';
			subText = type === 'measure' ? 'Планиране за реализацију у текућем кварталу' : 'Текући квартал + нереализовани од почетка примене НПАА';
			break;
		case 'totalPlannedCount':
			displayName = type === 'measure' ? 'Укупно планираних мера' : 'Укупан број прописа';
			subText = type === 'measure' ? 'Планиране за реализацију у текућем кварталу' : 'Планирани за реализацију у текућем кварталу';
			break;
		case 'realisedCount':
			displayName = type === 'measure' ? 'Реализовано (у текућем кварталу)' : 'Реализовано (у текућем кварталу)';
			subText = type === 'measure' ? `Мере са статусом "Објављен" у ${current}` : `Прописи са статусом "Објављен" у ${current}`;
			break;
		case 'previousQuarterUnrealisedCount':
			displayName = type === 'measure' ? 'Нереализовано од почетка примене НПАА' : 'Нереализовано од почетка примене НПАА';
			subText = type === 'measure' ? `Нереализовано од ${npaaFrom}` : `Нереализовано од ${npaaFrom}`;
			break;
		case 'totalUnrealisedCount':
			displayName = type === 'measure' ? 'Нереализовано (у текућем кварталу)' : 'Нереализовано (у текућем кварталу)';
			subText = type === 'measure' ? 'Преносе се аутоматски у наредни квартал' : `Преносе се аутоматски у наредни квартал`;
			break;
		default:
			break;
	}

	const formatLabel = (k: string = '') =>
		k
			.replace(/([A-Z])/g, ' $1')
			.replace(/^./, (s) => s?.toUpperCase())
			.replace('Count', '')
			.trim();

	const { bgClass, numberClass, mainTextClass, subTextClass } = getColorPallete(key);

	const labelToShow = displayName !== key ? displayName : formatLabel(key);
	const prevRef = useRef<number | null>(null);
	const rafRef = useRef<number | null>(null);
	const [animatedValue, setAnimatedValue] = useState<number>(0);

	const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

	useEffect(() => {
		if (typeof value !== 'number') {
			if (rafRef.current) {
				cancelAnimationFrame(rafRef.current);
				rafRef.current = null;
			}
			prevRef.current = null;
			setAnimatedValue(0);
			return;
		}

		const from = typeof prevRef.current === 'number' ? prevRef.current : 0;
		const to = value;
		const duration = 700;
		const start = performance.now();

		const step = (now: number) => {
			const elapsed = now - start;
			const t = Math.min(elapsed / duration, 1);
			const eased = easeOutCubic(t);
			const current = Math.round(from + (to - from) * eased);
			setAnimatedValue(current);

			if (t < 1) {
				rafRef.current = requestAnimationFrame(step);
			} else {
				prevRef.current = to;
				rafRef.current = null;
			}
		};

		// cancel any previous and start
		if (rafRef.current) cancelAnimationFrame(rafRef.current);
		rafRef.current = requestAnimationFrame(step);

		return () => {
			if (rafRef.current) {
				cancelAnimationFrame(rafRef.current);
				rafRef.current = null;
			}
		};
	}, [value]);

	const renderedValue = typeof value === 'number' ? animatedValue : String(value ?? '-');

	return (
		<div className={`px-4 py-3 flex flex-col ${bgClass}`}>
			<span className={`font-semibold ${numberClass}`}>{renderedValue}</span>
			<span className={`${mainTextClass}`}>{labelToShow}</span>

			<span className={`${subTextClass}`}>{subText}</span>
		</div>
	);
};

export default Counter;
